import React, { useState } from 'react';
import { Box, Button, Typography, Paper, Alert, LinearProgress, Chip, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { exportProductsCsv, importProductsCsv, exportVariantsCsv, importVariantsCsv } from '../api';

export default function BulkUpdate() {
  // Tab state
  const [tab, setTab] = useState(0);

  // Products CSV state
  const [prodFile, setProdFile] = useState(null);
  const [prodUploading, setProdUploading] = useState(false);
  const [prodResult, setProdResult] = useState(null);
  const [prodError, setProdError] = useState(null);

  // Variants CSV state
  const [varFile, setVarFile] = useState(null);
  const [varUploading, setVarUploading] = useState(false);
  const [varResult, setVarResult] = useState(null);
  const [varError, setVarError] = useState(null);

  const handleDownloadProducts = async () => {
    try {
      setProdError(null);
      const blob = await exportProductsCsv();
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'products_export.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setProdError('Failed to download products CSV.');
    }
  };

  const handleFileChangeProducts = (e) => {
    setProdFile(e.target.files?.[0] || null);
    setProdResult(null);
    setProdError(null);
  };

  const handleUploadProducts = async () => {
    if (!prodFile) return;
    setProdUploading(true);
    setProdError(null);
    setProdResult(null);
    try {
      const res = await importProductsCsv(prodFile);
      setProdResult(res);
    } catch (e) {
      setProdError(e?.response?.data?.error || 'Failed to import products CSV.');
    } finally {
      setProdUploading(false);
    }
  };

  const handleDownloadVariants = async () => {
    try {
      setVarError(null);
      const blob = await exportVariantsCsv();
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'product_variants_export.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setVarError('Failed to download variants CSV.');
    }
  };

  const handleFileChangeVariants = (e) => {
    setVarFile(e.target.files?.[0] || null);
    setVarResult(null);
    setVarError(null);
  };

  const handleUploadVariants = async () => {
    if (!varFile) return;
    setVarUploading(true);
    setVarError(null);
    setVarResult(null);
    try {
      const res = await importVariantsCsv(varFile);
      setVarResult(res);
    } catch (e) {
      setVarError(e?.response?.data?.error || 'Failed to import variants CSV.');
    } finally {
      setVarUploading(false);
    }
  };

  const getProductColumns = (result) => {
    const allRows = [...(result.details?.updated || []), ...(result.details?.failed || []), ...(result.details?.skipped || [])];
    const keys = new Set(['status', 'product_id', 'name', 'reason']);
    allRows.forEach(row => Object.keys(row).forEach(k => keys.add(k)));
    return Array.from(keys).filter(k => !['status', 'product_id', 'name', 'reason'].includes(k));
  };

  const getVariantColumns = (result) => {
    const allRows = [...(result.details?.updated || []), ...(result.details?.failed || []), ...(result.details?.skipped || [])];
    const keys = new Set(['status', 'variant_id', 'product_id', 'product_name', 'reason']);
    allRows.forEach(row => Object.keys(row).forEach(k => keys.add(k)));
    return Array.from(keys).filter(k => !['status', 'variant_id', 'product_id', 'product_name', 'reason'].includes(k));
  };
  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 130px)', minHeight: 0 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Bulk Update</Typography>
      <Paper sx={{ p: 1.5, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="fullWidth" sx={{ mb: 1.5 }}>
          <Tab label="Products CSV" />
          <Tab label="Variants CSV" />
        </Tabs>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Products panel */}
          {tab === 0 && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Products</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                <Button size="small" variant="contained" onClick={handleDownloadProducts}>Download CSV</Button>
                <Button size="small" variant="outlined" component="label">
                  {prodFile ? 'Change File' : 'Choose CSV'}
                  <input hidden type="file" accept=".csv" onChange={handleFileChangeProducts} />
                </Button>
                <Button size="small" variant="contained" disabled={!prodFile || prodUploading} onClick={handleUploadProducts}>Upload</Button>
                {prodUploading && <LinearProgress sx={{ width: 120, height: 6, borderRadius: 1 }} />}
                {prodError && <Alert sx={{ m: 0, p: 0.5 }} severity="error">{prodError}</Alert>}
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                Editable fields: name, slug, description, status, category_id, brand_id, barcode, hsn, tax_id, type, indicator, require_prescription, is_unlimited_stock, tax_included_in_price, cod_allowed, cancelable_status, return_status, return_days, total_allowed_quantity, till_status, is_approved, manufacturer, made_in, fssai_lic_no, tags, meta title/keywords/description, schema_markup. Identifier: product_id (required).
              </Typography>
              {prodResult && (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    <Chip size="small" label={`Total: ${prodResult.total || 0}`} />
                    <Chip size="small" color="success" label={`Updated: ${prodResult.updated || 0}`} />
                    <Chip size="small" color="error" label={`Failed: ${prodResult.failed || 0}`} />
                    <Chip size="small" color="info" label={`Skipped: ${prodResult.skipped || 0}`} />
                  </Box>
                  <TableContainer sx={{ flex: 1, overflow: 'auto', width: '100%' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Product ID</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                          {getProductColumns(prodResult).map(col => (
                            <TableCell key={col} sx={{ fontWeight: 'bold' }}>{col}</TableCell>
                          ))}
                          <TableCell sx={{ fontWeight: 'bold' }}>Reason</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(prodResult.details?.updated || []).map((r, i) => (
                          <TableRow key={`pu-${i}`}>
                            <TableCell sx={{ color: 'success.main', fontWeight: 'bold' }}>updated</TableCell>
                            <TableCell>#{r.product_id}</TableCell>
                            <TableCell>{r.name || ''}</TableCell>
                            {getProductColumns(prodResult).map(col => (
                              <TableCell key={col}>{r[col] ? String(r[col]).slice(0, 50) : ''}</TableCell>
                            ))}
                            <TableCell></TableCell>
                          </TableRow>
                        ))}
                        {(prodResult.details?.failed || []).map((r, i) => (
                          <TableRow key={`pf-${i}`}>
                            <TableCell sx={{ color: 'error.main', fontWeight: 'bold' }}>failed</TableCell>
                            <TableCell>#{r.product_id || ''}</TableCell>
                            <TableCell>{r.name || ''}</TableCell>
                            {getProductColumns(prodResult).map(col => (
                              <TableCell key={col}>{r[col] ? String(r[col]).slice(0, 50) : ''}</TableCell>
                            ))}
                            <TableCell>{r.reason || ''}</TableCell>
                          </TableRow>
                        ))}
                        {(prodResult.details?.skipped || []).map((r, i) => (
                          <TableRow key={`ps-${i}`}>
                            <TableCell sx={{ color: 'info.main', fontWeight: 'bold' }}>skipped</TableCell>
                            <TableCell>#{r.product_id || ''}</TableCell>
                            <TableCell>{r.name || ''}</TableCell>
                            {getProductColumns(prodResult).map(col => (
                              <TableCell key={col}>{r[col] ? String(r[col]).slice(0, 50) : ''}</TableCell>
                            ))}
                            <TableCell>{r.reason || ''}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}

          {/* Variants panel */}
          {tab === 1 && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Variants</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                <Button size="small" variant="contained" onClick={handleDownloadVariants}>Download CSV</Button>
                <Button size="small" variant="outlined" component="label">
                  {varFile ? 'Change File' : 'Choose CSV'}
                  <input hidden type="file" accept=".csv" onChange={handleFileChangeVariants} />
                </Button>
                <Button size="small" variant="contained" disabled={!varFile || varUploading} onClick={handleUploadVariants}>Upload</Button>
                {varUploading && <LinearProgress sx={{ width: 120, height: 6, borderRadius: 1 }} />}
                {varError && <Alert sx={{ m: 0, p: 0.5 }} severity="error">{varError}</Alert>}
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                Editable fields: product_short_name, type, measurement, price, purchase_price, discounted_price, stock, stock_unit_id, status. Identifier: variant_id (required). Read-only reference: product_name, product_barcode, unit_name, unit_short_code.
              </Typography>
              {varResult && (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    <Chip size="small" label={`Total: ${varResult.total || 0}`} />
                    <Chip size="small" color="success" label={`Updated: ${varResult.updated || 0}`} />
                    <Chip size="small" color="error" label={`Failed: ${varResult.failed || 0}`} />
                    <Chip size="small" color="info" label={`Skipped: ${varResult.skipped || 0}`} />
                  </Box>
                  <TableContainer sx={{ flex: 1, overflow: 'auto', width: '100%' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Variant ID</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
                          {getVariantColumns(varResult).map(col => (
                            <TableCell key={col} sx={{ fontWeight: 'bold' }}>{col}</TableCell>
                          ))}
                          <TableCell sx={{ fontWeight: 'bold' }}>Reason</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(varResult.details?.updated || []).map((r, i) => (
                          <TableRow key={`vu-${i}`}>
                            <TableCell sx={{ color: 'success.main', fontWeight: 'bold' }}>updated</TableCell>
                            <TableCell>v#{r.variant_id}</TableCell>
                            <TableCell>p#{r.product_id || ''} {r.product_name || ''}</TableCell>
                            {getVariantColumns(varResult).map(col => (
                              <TableCell key={col}>{r[col] ? String(r[col]).slice(0, 50) : ''}</TableCell>
                            ))}
                            <TableCell></TableCell>
                          </TableRow>
                        ))}
                        {(varResult.details?.failed || []).map((r, i) => (
                          <TableRow key={`vf-${i}`}>
                            <TableCell sx={{ color: 'error.main', fontWeight: 'bold' }}>failed</TableCell>
                            <TableCell>v#{r.variant_id || ''}</TableCell>
                            <TableCell>p#{r.product_id || ''} {r.product_name || ''}</TableCell>
                            {getVariantColumns(varResult).map(col => (
                              <TableCell key={col}>{r[col] ? String(r[col]).slice(0, 50) : ''}</TableCell>
                            ))}
                            <TableCell>{r.reason || ''}</TableCell>
                          </TableRow>
                        ))}
                        {(varResult.details?.skipped || []).map((r, i) => (
                          <TableRow key={`vs-${i}`}>
                            <TableCell sx={{ color: 'info.main', fontWeight: 'bold' }}>skipped</TableCell>
                            <TableCell>v#{r.variant_id || ''}</TableCell>
                            <TableCell>p#{r.product_id || ''} {r.product_name || ''}</TableCell>
                            {getVariantColumns(varResult).map(col => (
                              <TableCell key={col}>{r[col] ? String(r[col]).slice(0, 50) : ''}</TableCell>
                            ))}
                            <TableCell>{r.reason || ''}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
